import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, ShieldCheck, CalendarClock, Sparkles, MapPin, ArrowRight, Search } from "lucide-react";
import { api, fileUrl, formatINR } from "../lib/api";
import Seo from "../components/Seo";

/** Dedicated "New Projects" page — showcases under-construction + featured residential inventory.
 *  Falls back to featured + residential listings if no under-construction items exist. */

export default function NewProjects() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [city, setCity] = useState("");
    const [q, setQ] = useState("");

    useEffect(() => {
        setLoading(true);
        api.get("/properties", { params: { possession_status: "under_construction", limit: 50 } })
            .then(async (r) => {
                let list = r.data.items || [];
                // Fall back to featured residential if no under-construction items yet
                if (list.length === 0) {
                    const r2 = await api.get("/properties", { params: { featured: true, property_type: "residential", limit: 50 } });
                    list = r2.data.items || [];
                }
                setItems(list);
            })
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    const cities = useMemo(() => {
        const s = new Set();
        items.forEach((p) => { if (p.city) s.add(p.city); });
        return Array.from(s).sort();
    }, [items]);

    const filtered = useMemo(() => {
        return items.filter((p) => {
            if (city && (p.city || "").toLowerCase() !== city.toLowerCase()) return false;
            if (q) {
                const hay = `${p.title || ""} ${p.locality || ""} ${p.city || ""}`.toLowerCase();
                if (!hay.includes(q.toLowerCase())) return false;
            }
            return true;
        });
    }, [items, city, q]);

    return (
        <div className="min-h-screen bg-white">
            <Seo
                title="New Real-Estate Projects — Under Construction, RERA-Verified"
                description="Explore RERA-registered upcoming projects across Nagpur, Mumbai, Pune & 20+ cities. Under-construction apartments, plots and commercial spaces with launch prices and possession dates."
                path="/new-projects"
                keywords="new projects Nagpur, under construction property, RERA projects, upcoming apartments, launch prices, possession 2026 2027"
            />

            {/* HERO */}
            <section className="text-on-image relative bg-gradient-to-br from-[#001a3d] via-[#002657] to-[#12234b] border-b border-gold-500/10 overflow-hidden">
                <div className="absolute inset-0 grid-pattern opacity-25" />
                <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-24">
                    <div className="label-overline mb-3 inline-flex items-center gap-2">
                        <Building2 className="w-3.5 h-3.5" /> New Launches & Under Construction
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-3xl">
                        Book at <span className="text-gold-400">launch prices.</span><br />
                        Move in by 2027.
                    </h1>
                    <p className="text-white/75 text-base sm:text-lg mt-5 max-w-2xl">
                        Every project is <strong className="text-gold-300">RERA-registered</strong> with escrow-backed payments, verified possession dates and quarterly construction updates.
                    </p>

                    <div className="mt-8 flex flex-wrap gap-3">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-gold-500/40 text-xs font-bold text-gold-200">
                            <ShieldCheck className="w-3.5 h-3.5" /> RERA-Verified
                        </div>
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-gold-500/40 text-xs font-bold text-gold-200">
                            <CalendarClock className="w-3.5 h-3.5" /> Escrow-Backed
                        </div>
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur border border-gold-500/40 text-xs font-bold text-gold-200">
                            <Sparkles className="w-3.5 h-3.5" /> Direct-from-Developer
                        </div>
                    </div>
                </div>
            </section>

            {/* FILTERS */}
            <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-8 sm:py-10">
                <div className="rounded-2xl bg-[#FAFAF7] border border-navy-500/10 p-4 sm:p-5 flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="w-4 h-4 text-navy-500/40 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search project, locality or builder"
                            data-testid="new-projects-search"
                            className="w-full bg-white border border-navy-500/15 rounded-md pl-9 pr-3 py-2 text-sm text-navy-500 focus:border-gold-500/60 focus:outline-none"
                        />
                    </div>
                    {cities.length > 0 && (
                        <select
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            data-testid="new-projects-city"
                            className="bg-white border border-navy-500/15 rounded-md px-3 py-2 text-sm text-navy-500"
                        >
                            <option value="">All cities</option>
                            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    )}
                    <div className="text-sm text-navy-500/70">
                        <span className="font-bold text-navy-500">{filtered.length}</span> project{filtered.length === 1 ? "" : "s"}
                    </div>
                </div>
            </section>

            {/* GRID */}
            <section className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pb-16 sm:pb-24">
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {[1,2,3].map((i) => <div key={i} className="h-72 rounded-2xl shimmer" />)}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-20 rounded-2xl bg-[#FAFAF7] border border-navy-500/10">
                        <Building2 className="w-12 h-12 text-gold-500/60 mx-auto mb-4" />
                        <h3 className="font-heading text-2xl font-bold text-navy-500 mb-2">No live projects match your search</h3>
                        <p className="text-navy-500/60 text-sm mb-6">Try clearing filters or browsing all listings.</p>
                        <Link
                            to="/properties"
                            className="text-on-image inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-gold-500 text-white font-semibold hover:bg-gold-600"
                        >
                            Browse all properties <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6" data-testid="new-projects-grid">
                        {filtered.map((p, idx) => (
                            <Link
                                key={p.id}
                                to={`/properties/${p.id}`}
                                data-testid={`new-project-card-${p.id}`}
                                className={`group block rounded-2xl overflow-hidden bg-white border border-navy-500/10 hover:border-gold-500/50 hover-lift transition-colors card-enter card-enter-${Math.min(idx % 12, 11)}`}
                            >
                                <div className="relative aspect-[16/10]">
                                    <img
                                        src={p.images?.[0] ? fileUrl(p.images[0]) : "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"}
                                        alt={p.title}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-navy-950/70 to-transparent" />
                                    <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                                        {p.possession_status === "under_construction" && (
                                            <span className="px-2 py-0.5 rounded-full bg-orange-500 text-white text-[10px] font-bold uppercase tracking-widest shadow-sm">Under Construction</span>
                                        )}
                                        {p.featured && (
                                            <span className="px-2 py-0.5 rounded-full bg-gold-500 text-white text-[10px] font-bold uppercase tracking-widest shadow-sm inline-flex items-center gap-1">
                                                <ShieldCheck className="w-2.5 h-2.5" /> Featured
                                            </span>
                                        )}
                                        {p.verified && (
                                            <span className="px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-widest shadow-sm">RERA</span>
                                        )}
                                    </div>
                                </div>
                                <div className="p-4 space-y-2">
                                    <div className="text-[10px] uppercase tracking-widest text-gold-700 font-bold">
                                        {p.property_type}{p.subtype ? ` · ${p.subtype}` : ""}
                                    </div>
                                    <h3 className="font-heading text-lg font-bold text-navy-500 leading-snug line-clamp-1">{p.title}</h3>
                                    <div className="flex items-baseline justify-between gap-2">
                                        <div className="text-gold-600 font-heading font-extrabold text-xl">
                                            {formatINR(p.price)}
                                            {p.listing_type === "rent" && <span className="text-[11px] font-medium text-navy-500/60 ml-1">/mo</span>}
                                        </div>
                                        {p.possession_date && (
                                            <div className="text-[10px] text-navy-500/60 inline-flex items-center gap-1">
                                                <CalendarClock className="w-3 h-3 text-gold-600" />
                                                {new Date(p.possession_date).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-xs text-navy-500/60 flex items-center gap-1">
                                        <MapPin className="w-3 h-3 text-gold-600" />
                                        <span className="line-clamp-1">{[p.locality, p.city].filter(Boolean).join(", ")}</span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
